import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { configurationSettings, getItemById, getOrderItemCount, getOrderItemCountByItemId, getOrderItemPriceForPriceRestriction, getOrderPriceRestrictionsForItem, getPriceRestrictionFromLowestItemPrice, isNormalItem, orderHasMaximumAllowedQuantityOfItemPrice } from '@vista/omnichannel-components-domain';
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { observer } from 'mobx-react';
import { useContext } from 'react';
import { SingleItemPickerContextInstance } from '../types/context';
import { SingleItemPickerItem } from './item';
const b = bemClassBuilder('v-single-item-picker-item-list');
export const SingleItemPickerItemList = observer(({ sellableItem }) => {
    const validRestrictions = getValidItemPriceRestrictions(sellableItem);
    const { config } = useContext(SingleItemPickerContextInstance);
    const defaultPricedItemCount = getOrderItemCountByItemId(sellableItem.itemId, {});
    const allowedMaximum = !config.quantityLimit.isUnlimited
        ? config.quantityLimit.maximum
        : configurationSettings.data.order.maximumItemsPerOrder;
    const orderHasMaxAllowedItems = getOrderItemCount() >= allowedMaximum;
    /**
     * Show the default priced item IF:
     * - There are no items with discounts.
     * OR
     * - The max number of items at the discounted price have been added and the
     * order item limit has not been reached.
     */
    const showDefaultPricedItem = !validRestrictions.length ||
        (isMaximumAllowedSelectionReachedForAllDiscounts(validRestrictions, sellableItem) &&
            !orderHasMaxAllowedItems) ||
        !!defaultPricedItemCount;
    const item = getItemById(sellableItem.itemId);
    if (!item) {
        throw new Error(`Failed to render SingleItemPickerItemList. Could not find item with ID: ${sellableItem.itemId}`);
    }
    if (!isNormalItem(item))
        return null; // We only support normal items currently
    return (_jsxs("ul", Object.assign({ className: b.block() }, { children: [validRestrictions.map((validRestriction, index) => (_jsx(SingleItemPickerItem, { sellableItem: sellableItem, priceRestriction: validRestriction }, `${sellableItem.itemId}_discount-${index}`))), showDefaultPricedItem && _jsx(SingleItemPickerItem, { sellableItem: sellableItem, priceRestriction: {} })] })));
});
SingleItemPickerItemList.displayName = 'SingleItemPickerItemList';
const isMaximumAllowedSelectionReachedForAllDiscounts = (priceRestrictions, sellableItem) => {
    if (!priceRestrictions.length)
        return false;
    return priceRestrictions.every(priceRestriction => {
        const discountedOrderItemPrice = getOrderItemPriceForPriceRestriction(sellableItem, priceRestriction);
        return (discountedOrderItemPrice &&
            orderHasMaximumAllowedQuantityOfItemPrice(sellableItem.itemId, discountedOrderItemPrice, getOrderItemCountByItemId(sellableItem.itemId, priceRestriction)));
    });
};
const getValidItemPriceRestrictions = (sellableItem) => {
    const itemPriceRestriction = getPriceRestrictionFromLowestItemPrice(sellableItem);
    const orderItemPriceRestrictions = getOrderPriceRestrictionsForItem(sellableItem.itemId);
    if (!itemPriceRestriction)
        return orderItemPriceRestrictions;
    const discountIds = orderItemPriceRestrictions.map(restriction => restriction.discountId);
    const rewardIds = orderItemPriceRestrictions.map(restriction => restriction.rewardId);
    const hasDifferentRestrictionFromOrderItems = !!(itemPriceRestriction.discountId && !discountIds.includes(itemPriceRestriction.discountId)) ||
        !!(itemPriceRestriction.rewardId && !rewardIds.includes(itemPriceRestriction.rewardId));
    const validRestrictions = hasDifferentRestrictionFromOrderItems
        ? [...orderItemPriceRestrictions, itemPriceRestriction]
        : orderItemPriceRestrictions;
    return validRestrictions;
};
